/**
 * API error helpers (WEBD-19).
 *
 * Every API failure carries one envelope — `{ success: false, error: { code,
 * message, details? } }` (shared `ApiErrorResponse`), from routes, the error
 * handler and the not-found handler alike. These read it off an axios error.
 * Kept apart from the axios client so feature code can use them without
 * importing the client itself.
 */

import type { ApiErrorResponse } from '@luminaria/shared';
import axios from 'axios';

/** The envelope's `error` object, when the failed response carries one. */
export function getApiError(error: unknown): ApiErrorResponse['error'] | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const body: unknown = error.response?.data;
  if (typeof body !== 'object' || body === null || !('error' in body)) return undefined;
  const envelope = (body as { error: unknown }).error;
  if (typeof envelope !== 'object' || envelope === null) return undefined;
  const { code, message, details } = envelope as { code?: unknown; message?: unknown; details?: unknown };
  if (typeof message !== 'string' && typeof code !== 'string') return undefined;
  return {
    code: typeof code === 'string' ? code : '',
    message: typeof message === 'string' ? message : '',
    ...(details !== undefined ? { details } : {}),
  };
}

interface ErrorMessageOptions {
  /** Append the envelope's machine code, e.g. "Brokerage unavailable (SNAPTRADE_ERROR)". */
  includeCode?: boolean;
}

/**
 * Extract the user-facing error message from an API error response.
 */
export function getErrorMessage(error: unknown, options: ErrorMessageOptions = {}): string {
  const apiError = getApiError(error);
  if (apiError) {
    const message = apiError.message || apiError.code;
    if (options.includeCode && apiError.code && apiError.message) {
      return `${apiError.message} (${apiError.code})`;
    }
    if (message) return message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/** HTTP status of a failed request, when there was a response. */
export function getErrorStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}
